import { createClient, type ClientConfig, type FilterDefault } from "@sanity/client";
const defaultApiVersion = "2025-10-21";
const defaultStudioUrl = "/studio";
type SanityClientOptions = Pick<ClientConfig, "perspective" | "stega" | "token" | "useCdn">;
export const sanityProjectId = process.env.SANITY_PROJECT_ID || process.env.SANITY_STUDIO_PROJECT_ID;
export const sanityDataset = process.env.SANITY_DATASET || process.env.SANITY_STUDIO_DATASET;
export const sanityApiVersion = process.env.SANITY_API_VERSION || defaultApiVersion;
export const sanityStudioUrl = process.env.SANITY_STUDIO_URL || defaultStudioUrl;

/** Shared read-token precedence for every server-side Sanity client. */
export function getSanityReadToken(token?: string) {
  return token || process.env.SANITY_API_TOKEN_READ_ONLY || process.env.SANITY_API_TOKEN;
}

/**
 * Parse a boolean-ish env var. Accepts the common truthy/falsy spellings so it
 * is hard to misconfigure; anything unset, empty, or unrecognised returns
 * `fallback`. Mirrors the tolerant parsing in `cache.server.ts`.
 */
function parseBooleanEnv(value: string | undefined, fallback: boolean): boolean {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) {
    return fallback;
  }
  if (normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on") {
    return true;
  }
  if (normalized === "0" || normalized === "false" || normalized === "no" || normalized === "off") {
    return false;
  }
  return fallback;
}

/**
 * Default `useCdn`, read from `SANITY_USE_CDN`. Chooses between Sanity's edge
 * CDN (`apicdn.sanity.io` — cached, eventually-consistent, cheaper) and the
 * uncached API (`api.sanity.io` — strongly consistent). Defaults to `false`
 * because cached published reads rely on live-API sync tags rather than
 * eventual CDN consistency. Set `SANITY_USE_CDN=true` to trade
 * that publish-freshness for cheaper cached reads (e.g. performance testing).
 *
 * This is only the *default*: preview/draft-mode reads always force
 * `useCdn: false` (drafts are never served from the CDN), and any caller may
 * override per request.
 */
export const sanityUseCdn = parseBooleanEnv(process.env.SANITY_USE_CDN, false);

/**
 * Stega encoding appends invisible metadata to rendered strings so the visual
 * editor can draw click-to-edit overlays. That metadata breaks any string used
 * for logic, attributes, or DOM ids rather than display.
 *
 * The library's default filter already skips `_`-prefixed keys (`_type`,
 * `_key`, `_ref`), URLs/dates, slugs, and a denylist that includes `theme`,
 * `layout`, `color`, `hex`, `icon`, `href`, `url`, `link`, `status`. This adds
 * the leaf fields specific to this codebase that the default would encode but
 * which we consume as logic/ids — verified by a live preview pass (these are
 * the only raw Sanity string fields used in `X[field]` className maps,
 * `field === "..."` comparisons, `switch`, or set/parse logic):
 *   - `mode`            section light/dark comparison
 *   - `anchorName`      DOM id + sub-nav anchor target
 *   - `sectionId.current` DOM id (slug object, so the leaf is `current`)
 *   - `eventName.current` Customer.io event name POSTed on form submit (slug
 *                       object under a field NOT named `slug`, so the default
 *                       filter would encode it and corrupt submissions)
 *   - `aspectRatio`     parsed via `toSupportedRatio` set-membership
 *   - `kind`            link/media discriminator (switch)
 *   - `contentPlacement` gallery layout discriminator
 *   - `navTheme`        header style className map (`style[navTheme]`)
 *   - `subNavMode`      sub-nav style className map
 *   - `mediaPosition`   `=== "right"` column-order logic in text/media blocks
 *   - `pagePattern`     page background variant, keys `patterns[…]` in
 *                       `features/app/pattern.tsx`; an encoded value misses the
 *                       lookup and the pattern layer renders nothing at all
 *   - `footerPattern`   footer variant comparison (same `patterns[…]` map)
 *   - `variant`         link/button style variant (switch/comparison)
 *   - `linkVariant`     keys `getVariants()[variant]` in `features/ui/button`. Note the
 *                       lookup misses silently: `variant = "primary"` only defaults on
 *                       `undefined`, so an encoded value yields NO variant styles at all
 *   - `supportLinkVariant` same, for the header support link
 *   - `pageMode`        `=== "landing"` picks the whole layout in `app/app-layout.tsx`
 *   - `assetLayout`     keys `assetLayoutVariants[…]` in the media-only section
 *   - `spacingBelow`    `=== "more"` gate on the inter-section gap in
 *                       `page-builder/renderer.tsx`; encoded, the gap is dropped
 * Matching is on the exact leaf field name, so a prefixed field needs its own entry —
 * `variant` does not cover `linkVariant`. Every `options.list` enum consumed as a key or
 * a comparison belongs here; see the audit note in vault/visual-editing.md.
 * Derived values (`chromeTheme`, `surface`, `megaSurface`, `fit`,
 * `buttonVariant`) inherit cleanliness from these and need no entry.
 *
 * Centralising this here (vs. cleaning per-adapter) keeps logic strings clean
 * across every fetch path — including ones the page-builder serializer never
 * sees, such as the layout sub-nav anchors in `layout/adapter.ts`. Note: this
 * is a denylist of field names with no type link to the schema — if a new enum
 * field is consumed as logic, add it here (see vault/visual-editing.md).
 */
const stegaLogicLeafFields = new Set(["mode", "anchorName", "aspectRatio", "kind", "contentPlacement", "navTheme", "subNavMode", "mediaPosition", "pagePattern", "footerPattern", "variant", "linkVariant", "supportLinkVariant", "pageMode", "assetLayout", "columns", "spacingBelow"]);

/**
 * `slug` fields whose `current` value is consumed as logic, never displayed:
 *   - `sectionId`     the section's DOM id and sub-nav anchor target
 *   - `eventName`     the analytics event key
 *   - `questionSlug`  the FAQ question's DOM id, URL hash and JSON-LD `url`
 * Encoded, these ship zero-width characters inside `id=""` and `location.hash`,
 * and hash matching silently stops working in preview.
 */
const stegaLogicSlugFields = new Set(["sectionId", "eventName", "questionSlug"]);
export const sanityStegaFilter: FilterDefault = props => {
  const leaf = props.sourcePath.at(-1);
  if (typeof leaf === "string" && stegaLogicLeafFields.has(leaf)) {
    return false;
  }
  if (leaf === "current" && stegaLogicSlugFields.has(props.sourcePath.at(-2) as string)) {
    return false;
  }
  return props.filterDefault(props);
};
export function getSanityClient(options: SanityClientOptions = {}) {
  if (typeof window !== "undefined") {
    throw new Error("Sanity loaders must run on the server.");
  }
  const token = getSanityReadToken(options.token);
  return createClient({
    projectId: sanityProjectId,
    dataset: sanityDataset,
    apiVersion: sanityApiVersion,
    token,
    useCdn: options.useCdn ?? sanityUseCdn,
    perspective: options.perspective ?? "published",
    stega: options.stega ?? {
      enabled: false,
      studioUrl: sanityStudioUrl
    }
  });
}